import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import contactApi from '../../services/contactApi.js';
import newsletterApi from '../../services/newsletterApi.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';
import { toast } from 'react-toastify';
import { BrandSocialIcon, brandSocialLinks } from './ShopSocialBar.js';
import LeadershipAvatar from './LeadershipAvatar.js';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const REASONS = [
  'General Inquiry',
  'Subscription Issue',
  'Delivery Problem',
  'Digital Download Help',
  'Refund Request',
  'Business / Advertise',
  'Press & Media',
  'Other',
];

function NewsletterForm({ source = 'Contact Sidebar' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.email && !form.phone) { toast.warn('Please enter either your email or phone number.'); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.warn('Please enter a valid email address.'); return; }
    if (form.phone && form.phone.replace(/\D/g, '').length < 10) { toast.warn('Please enter a valid phone number (at least 10 digits).'); return; }
    setLoading(true);
    try {
      const r = await newsletterApi.subscribe({ ...form, source });
      if (r.duplicate) toast.warn(`Already subscribed! Welcome back, ${r.record.name || 'friend'}.`);
      else toast.success(`✅ Subscribed! Welcome aboard, ${r.record.name || 'friend'}!`);
      setForm({ name: '', email: '', phone: '' });
    } catch (e) { toast.error(e.message || 'Failed to subscribe, please try again.'); }
    finally { setLoading(false); }
  };
  return (
    <form className="grid grid-cols-1 gap-2" onSubmit={submit}>
      <input
        type="text"
        value={form.name}
        onChange={set('name')}
        placeholder="Your name"
        className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 bg-white outline-none focus:border-red-400 font-medium text-sm text-gray-900 placeholder:text-gray-400"
      />
      <input
        type="email"
        value={form.email}
        onChange={set('email')}
        placeholder="you@email.com"
        className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 bg-white outline-none focus:border-red-400 font-medium text-sm text-gray-900 placeholder:text-gray-400"
      />
      <div className="flex gap-2">
        <input
          type="tel"
          value={form.phone}
          onChange={set('phone')}
          placeholder="+92 300 1234567"
          className="flex-1 px-3.5 py-2.5 rounded-xl border-2 border-gray-200 bg-white outline-none focus:border-red-400 font-medium text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2.5 rounded-xl text-white font-black text-sm shadow disabled:opacity-60"
          style={{ backgroundColor: FLARE_RED }}
        >
          {loading ? '…' : 'Join'}
        </button>
      </div>
    </form>
  );
}

const FLARE_MAP_SHORT = 'https://maps.app.goo.gl/nfMM1mceaq3LndTC6';
const FLARE_MAP_EMBED = 'https://maps.google.com/maps?q=31.4925992,74.312348+(Flare+Magazine)&z=17&output=embed';

function isFlareHqMap(url) {
  const raw = String(url || '');
  return /maps\.app\.goo\.gl\/nfMM1mceaq3LndTC6/i.test(raw) || /flare\s*\+?\s*magazine/i.test(raw);
}

function mapsHref(url) {
  const raw = String(url || '').trim();
  if (!raw || isFlareHqMap(raw)) return FLARE_MAP_SHORT;
  return raw;
}

function mapsEmbedSrc(url) {
  const raw = String(url || '').trim();
  if (!raw || isFlareHqMap(raw)) return FLARE_MAP_EMBED;
  if (/output=embed/i.test(raw) || /\/maps\/embed/i.test(raw)) return raw;

  const place = raw.match(/\/place\/([^/?#]+)\/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (place) {
    const name = decodeURIComponent(place[1].replace(/\+/g, ' '));
    return `https://maps.google.com/maps?q=${place[2]},${place[3]}+(${encodeURIComponent(name)})&z=17&output=embed`;
  }
  const at = raw.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) {
    return `https://maps.google.com/maps?q=${at[1]},${at[2]}&z=17&output=embed`;
  }

  try {
    const u = new URL(raw);
    const q = u.searchParams.get('q') || u.searchParams.get('query');
    if (q) return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
  } catch {
    /* treat as address */
  }
  return `https://www.google.com/maps?q=${encodeURIComponent(raw)}&output=embed`;
}

function personTel(phone) {
  return String(phone || '').replace(/[^\d+]/g, '');
}

function teamGridClass(count) {
  if (count >= 4) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  if (count === 3) return 'grid-cols-1 lg:grid-cols-3';
  return 'grid-cols-1 sm:grid-cols-2';
}

function ShopContactSection() {
  const brand = useSiteBrand();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    reason: 'General Inquiry',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e && e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrors({});
    try {
      const res = await contactApi.submitRequest({ ...form, source: 'Website Contact' });
      setDone(res);
      toast.success('✅ Your message was sent to our team!');
      setForm({ name: '', email: '', phone: '', subject: '', reason: 'General Inquiry', message: '' });
    } catch (err) {
      const list = err?.details || [err?.message || 'Could not send your message. Please try again.'];
      const map = {};
      if (/name/i.test(list[0])) map.name = list[0];
      if (/email/i.test(list[0])) map.email = list[0];
      if (/subject/i.test(list[0])) map.subject = list[0];
      if (/message/i.test(list[0])) map.message = list[0];
      setErrors(map);
      toast.error(list[0]);
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      title: 'Email Us',
      sub: 'We reply in under 12 hours',
      icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      value: brand.email,
      href: brand.mailHref,
      color: 'from-indigo-500 to-blue-600',
    },
    {
      title: 'Call / WhatsApp',
      sub: 'Mon – Sun · 10am to 9pm PKT',
      icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
      value: brand.phone,
      href: brand.waHref || brand.telHref,
      color: 'from-emerald-500 to-green-600',
    },
    {
      title: brand.officeCardTitle || 'Visit Our Office',
      sub: brand.officeCardNote || 'By appointment only',
      icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
      value: brand.officeCardLocation || 'Gulberg III, Lahore, Pakistan',
      href: mapsHref(brand.officeMapUrl),
      color: 'from-amber-500 to-orange-600',
    },
  ];
  const team = Array.isArray(brand.contactTeam) ? brand.contactTeam : [];

  const socialLinks = brandSocialLinks(brand);

  return (
    <ShopLayout>
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fff5f5 0%, #ffffff 55%, #fff5f5 100%)' }}>
        <div className="pointer-events-none absolute -top-16 -left-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(196,21,28,0.18), transparent 70%)' }} />
        <div className="pointer-events-none absolute top-32 -right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.14), transparent 70%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-14 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-white shadow-sm border border-gray-100 mb-6" style={{ color: FLARE_RED }}>
            <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-3.5 w-3.5" />
            Contact Us
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mb-5 leading-tight">
            We’d love to <span style={{ color: FLARE_RED }}>hear from you</span>.
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-600 font-medium leading-relaxed">
            Questions about an issue, delivery, subscription, or advertisement? Send us a message — the Flare team replies within a few hours.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {contactInfo.map((c) => (
            <a
              key={c.title}
              href={c.href}
              target={c.href.startsWith('http') ? '_blank' : undefined}
              rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="group relative bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-gradient-to-br ${c.color} shadow-lg`}>
                <Icon path={c.icon} className="h-7 w-7 text-white" />
              </div>
              <div className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">{c.title}</div>
              <div className="font-black text-gray-900 text-xl mb-1">{c.value}</div>
              <div className="text-sm text-gray-500 font-semibold">{c.sub}</div>
            </a>
          ))}
        </div>

        {team.length > 0 && (
          <section className="mb-12 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 sm:px-8 pt-6 pb-4 flex items-center gap-3">
              <span className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: FLARE_RED }}>Leadership</span>
              <span className="flex-1 h-px bg-gray-100" />
            </div>
            <div className={`grid ${teamGridClass(team.length)} divide-y lg:divide-y-0 lg:divide-x divide-gray-100`}>
              {team.map((p, i) => {
                const tel = personTel(p.phone);
                return (
                  <article key={`${p.email || p.name}-${i}`} className="px-6 py-7 flex flex-col items-center text-center min-w-0">
                    <LeadershipAvatar index={i} name={p.name} photo={p.photo} />
                    <h3 className="mt-4 text-[17px] font-black tracking-tight text-gray-900 leading-snug">{p.name}</h3>
                    {p.title ? (
                      <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.16em] leading-relaxed px-1" style={{ color: FLARE_RED }}>
                        {p.title}
                      </p>
                    ) : null}
                    <div className="mt-4 w-full space-y-2">
                      {p.email ? (
                        <a
                          href={`mailto:${p.email}`}
                          title={p.email}
                          className="flex items-center justify-center gap-2 text-[13px] font-semibold text-gray-500 hover:text-gray-900 min-w-0"
                        >
                          <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-4 w-4 shrink-0 text-gray-400" />
                          <span className="truncate">{p.email}</span>
                        </a>
                      ) : null}
                      {p.phone ? (
                        <a
                          href={tel ? `tel:${tel}` : undefined}
                          className="flex items-center justify-center gap-2 text-[13px] font-semibold text-gray-500 hover:text-gray-900"
                        >
                          <Icon path="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" className="h-4 w-4 shrink-0 text-gray-400" />
                          <span>{p.phone}</span>
                        </a>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-10">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-7">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 mb-1">Send us a message</h2>
                <p className="text-gray-500 font-medium">Fill in the form below. Request ID and status are emailed to you automatically.</p>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Team Online
              </div>
            </div>

            {done?.ok && (
              <div className="mb-7 p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow">
                    <Icon path="M5 13l4 4L19 7" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-black text-emerald-900 text-lg mb-0.5">Message Received! 🎉</div>
                    <div className="text-sm text-emerald-800 font-semibold">
                      Reference ID: <span className="font-mono bg-white px-2 py-0.5 rounded-lg border border-emerald-200">{done.record.id}</span>
                      {done.stored === 'local' && done.note && (
                        <span className="block mt-1.5 text-emerald-700/90 text-xs">💡 {done.note}</span>
                      )}
                    </div>
                    <div className="text-sm text-emerald-700 mt-1.5">
                      Our team will reply at <b>{done.record.email}</b> shortly.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-black text-gray-800 mb-2">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="e.g. Ahmed Khan"
                    className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.name ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`}
                    required
                  />
                  {errors.name && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-black text-gray-800 mb-2">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="you@example.com"
                    className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.email ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`}
                    required
                  />
                  {errors.email && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-black text-gray-800 mb-2">Phone / WhatsApp <span className="text-gray-400 font-bold">(optional)</span></label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="+92 300 1234567"
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-gray-800 mb-2">Reason</label>
                  <div className="relative">
                    <select
                      value={form.reason}
                      onChange={(e) => set('reason', e.target.value)}
                      className="w-full px-4 pr-10 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white outline-none transition-colors font-medium text-gray-900 appearance-none cursor-pointer"
                    >
                      {REASONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                      <Icon path="M19 9l-7 7-7-7" className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-gray-800 mb-2">Subject Line <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => set('subject', e.target.value)}
                  placeholder="Briefly describe the issue or request"
                  className={`w-full px-4 py-3.5 rounded-xl border-2 ${errors.subject ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400`}
                  required
                />
                {errors.subject && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.subject}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-black text-gray-800">Your Message <span className="text-red-500">*</span></label>
                  <span className={`text-[11px] font-black uppercase tracking-widest ${form.message.length >= 500 ? 'text-red-600' : 'text-gray-400'}`}>
                    {form.message.length} / 2000
                  </span>
                </div>
                <textarea
                  value={form.message}
                  onChange={(e) => set('message', e.target.value.slice(0, 2000))}
                  placeholder="Tell us what we can help you with… order number, issue month, subscriber ID — the more detail the faster we can respond!"
                  rows={7}
                  className={`w-full px-4 py-3.5 rounded-xl border-2 resize-y min-h-[180px] ${errors.message ? 'border-red-400 bg-red-50/40' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300 focus:border-red-400 focus:bg-white'} outline-none transition-colors font-medium text-gray-900 placeholder:text-gray-400 leading-relaxed`}
                  required
                />
                {errors.message && <p className="text-sm text-red-600 font-bold mt-1.5">{errors.message}</p>}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-2">
                <div className="text-xs font-bold text-gray-500 leading-relaxed">
                  <div>🔒 Your info is safe. We never share contact details.</div>
                  <div>Response times: weekdays 2–4h · weekends 4–12h.</div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl text-white font-black shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
                  style={{ backgroundColor: FLARE_RED, boxShadow: '0 12px 30px -12px rgba(196,21,28,0.5)' }}
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-[3px] border-white/40" style={{ borderTopColor: '#fff', animation: 'flare-spin 0.8s linear infinite' }} />
                      Sending Message…
                    </>
                  ) : (
                    <>
                      <Icon path="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" className="h-5 w-5" />
                      Send Message to Team
                    </>
                  )}
                </button>
              </div>
              <style>{`@keyframes flare-spin { to { transform: rotate(360deg); } }`}</style>
            </form>
          </div>

          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 overflow-hidden relative" style={{ background: `linear-gradient(180deg, #fff 0%, #fff5f5 100%)` }}>
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full" style={{ background: 'radial-gradient(circle, rgba(196,21,28,0.18), transparent 70%)' }} />
              <div className="relative">
                <h3 className="text-xl font-black tracking-tight text-gray-900 mb-4">Follow us for updates</h3>
                <p className="text-sm text-gray-600 font-medium mb-6">
                  New issue drops every month — be the first to see cover reveals, excerpts & giveaways.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  {socialLinks.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="transition-transform hover:-translate-y-0.5 hover:scale-105"
                      aria-label={s.label}
                      title={s.label}
                    >
                      <BrandSocialIcon network={s} size={44} />
                    </a>
                  ))}
                </div>
                <div className="rounded-2xl border-2 border-dashed border-gray-200 p-5 bg-white/60 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: FLARE_RED }}>
                      <Icon path="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-black text-gray-900">Flash Newsletter</div>
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Monthly issue drops</div>
                    </div>
                  </div>
                  <NewsletterForm source="Contact Sidebar" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8">
              <h3 className="text-xl font-black tracking-tight text-gray-900 mb-4">Before you reach out…</h3>
              <ul className="space-y-3">
                {[
                  { t: 'Track delivery or order', l: 'See your Customer Area', href: '/#features' },
                  { t: 'Subscription plans & pricing', l: 'View all plans', href: '/plans' },
                  { t: 'Read any issue instantly', l: 'Browse magazines', href: '/magazines' },
                  { t: 'How ordering & delivery works', l: 'How it works guide', href: '/how' },
                ].map((x, i) => (
                  <li key={i}>
                    <Link to={x.href} className="group flex items-center justify-between gap-3 p-4 rounded-2xl border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition-colors">
                      <div>
                        <div className="font-black text-gray-900">{x.t}</div>
                        <div className="text-sm font-bold" style={{ color: FLARE_RED }}>{x.l} →</div>
                      </div>
                      <Icon path="M9 5l7 7-7 7" className="h-5 w-5 text-gray-400 group-hover:text-red-600 transition-colors" style={{}} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div
          className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-100"
          style={{ minHeight: '380px' }}
        >
          <div className="absolute inset-0 bg-gray-100" />
          <iframe
            title="Flare Magazine Office Map"
            className="absolute inset-0 w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={mapsEmbedSrc(brand.officeMapUrl)}
          />
          <div className="absolute top-5 left-5 sm:top-7 sm:left-7 bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 max-w-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                <Icon path="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-black text-gray-900">{brand.officeHqTitle || 'Flare Magazine HQ'}</div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{brand.officeHqNote || 'Visit us by appointment'}</div>
              </div>
            </div>
            <p className="text-sm text-gray-700 font-semibold leading-relaxed">
              {brand.officeHqAddress || '2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan'}
            </p>
          </div>
        </div>
      </section>
    </ShopLayout>
  );
}

export default ShopContactSection;
